/**
 * Pokéball glyph button (top-right of canvas) + the popover it opens.
 * Replaces the prior ☰ button. Three rows:
 *
 *   - Pokédex   active → launches PokedexScene
 *   - Bag       active → launches BagScene (enabled in bucket G PR 3;
 *                        was disabled with a "Later" subtitle until
 *                        the item economy landed)
 *   - Settings  active → launches SettingsScene (renamed from
 *                        MenuScene in step 7a)
 *
 * Lifecycle parallels PartyOrbs:
 *   - mountPokeballMenu(scene) attaches the glyph + popover DOM, plus
 *     SLEEP/WAKE listeners that toggle display.
 *   - handle.destroy() removes everything.
 *
 * The glyph button gets glass treatment matching the party orbs (same
 * backdrop-filter blur + saturate; the lens look stays consistent
 * across the chrome surface). The popover renders as a sibling DOM
 * element layered above the glyph.
 *
 * Dismissal:
 *   - tap-outside → capture-phase document pointerdown listener (not
 *     bubble-phase, so we beat any other handler that might
 *     stopPropagation). Idempotent.
 *   - scene change (one of the rows launched a scene) → SLEEP listener
 *     fires too, but we explicitly close before launch as well so
 *     there's no flicker.
 */
import Phaser from 'phaser';
import { pokeballGlyphElement } from './pokeballGlyph';
import { Z_OVERLAY_GLYPHS, Z_POPOVERS } from './zLayers';
import { effectsReduced } from '../render/motion';

const GLYPH_ID = 'poke-pokeball-glyph';
const POPOVER_ID = 'poke-popover';

const GLYPH_SIZE = 38;
const GLYPH_INNER_SIZE = 22;
const GLYPH_TOP_INSET = 12;
const GLYPH_RIGHT_INSET = 12;

export interface PokeballMenuHandle {
  destroy(): void;
}

export function mountPokeballMenu(scene: Phaser.Scene): PokeballMenuHandle {
  const parent = document.getElementById('game') ?? document.body;
  const worldScene = scene as Phaser.Scene & {
    setModalOverlayOpen?: (
      open: boolean,
      source: 'settings' | 'pokedex' | 'bag',
    ) => void;
    launchFrozenModal?: (
      sceneKey: 'PokedexScene' | 'SettingsScene' | 'BagScene',
      data: Record<string, unknown>,
    ) => Promise<void>;
  };

  /**
   * Glass-Over-World PR 3b: reduced-motion → frozen (snapshot + sleep);
   * default → live (transparent overlay, world keeps rendering).
   *
   * TODOs #24: when a Visual Richness Medium/Low setting ships, also
   * return true here. See craig-main-design-20260512-191925.md §9.C.
   */
  function shouldUseFrozenPath(): boolean {
    return effectsReduced();
  }

  function launchOverlay(
    sceneKey: 'PokedexScene' | 'SettingsScene' | 'BagScene',
    source: 'pokedex' | 'settings' | 'bag',
  ): void {
    if (shouldUseFrozenPath()) {
      // Frozen path: WorldScene snapshots, sleeps, launches the modal
      // with snapshotHandle in data. The modal's close() restores via
      // scene.stop + scene.wake('WorldScene').
      void worldScene.launchFrozenModal?.(sceneKey, {});
    } else {
      // Live path: WorldScene stays awake; the modal renders as a
      // transparent overlay on top. setModalOverlayOpen flips the
      // joystick/dialogue/orb suppress chain and (gated on
      // !dialogueOpen) clears any in-flight movement intent.
      worldScene.setModalOverlayOpen?.(true, source);
      scene.scene.launch(sceneKey);
    }
  }

  // ─── Glyph button ────────────────────────────────────────────────────

  const glyph = document.createElement('button');
  glyph.id = GLYPH_ID;
  glyph.type = 'button';
  glyph.setAttribute('aria-label', 'Menu');
  glyph.style.position = 'absolute';
  glyph.style.top = `max(${GLYPH_TOP_INSET}px, calc(env(safe-area-inset-top) + 4px))`;
  glyph.style.right = `${GLYPH_RIGHT_INSET}px`;
  glyph.style.width = `${GLYPH_SIZE}px`;
  glyph.style.height = `${GLYPH_SIZE}px`;
  glyph.style.padding = '0';
  glyph.style.borderRadius = '50%';
  glyph.style.border = '1.5px solid var(--specular-edge)';
  // Dark glass fill + soft top-highlight gradient. Was a peach-tinted
  // cream gradient pre-Future-Cartridge; now mirrors the orb chrome's
  // dark glass aesthetic. Light gradient stop kept so the glyph reads
  // as a physical lens, not a flat disc.
  glyph.style.background =
    'radial-gradient(circle at 50% 30%, rgba(255, 255, 255, 0.12) 0%, rgba(255, 255, 255, 0) 70%), var(--glass-fill-mid)';
  glyph.style.backdropFilter = 'blur(8px) saturate(1.4)';
  (glyph.style as unknown as { webkitBackdropFilter: string }).webkitBackdropFilter =
    'blur(8px) saturate(1.4)';
  glyph.style.boxShadow = [
    'inset 0 1px 0 var(--specular-edge)',
    'var(--depth-shadow)',
  ].join(', ');
  glyph.style.display = 'flex';
  glyph.style.alignItems = 'center';
  glyph.style.justifyContent = 'center';
  glyph.style.cursor = 'pointer';
  glyph.style.zIndex = String(Z_OVERLAY_GLYPHS);
  glyph.style.touchAction = 'manipulation';
  glyph.style.userSelect = 'none';
  (glyph.style as unknown as { webkitUserSelect: string }).webkitUserSelect = 'none';
  glyph.appendChild(
    pokeballGlyphElement({ size: GLYPH_INNER_SIZE, color: 'var(--text-primary)' }),
  );
  parent.appendChild(glyph);

  // ─── Popover ────────────────────────────────────────────────────────

  let popover: HTMLDivElement | null = null;
  let documentDismissListener: ((e: PointerEvent) => void) | null = null;
  let documentKeydownListener: ((e: KeyboardEvent) => void) | null = null;

  function isPopoverOpen(): boolean {
    return popover !== null;
  }

  function openPopover(): void {
    if (popover) return;
    const el = document.createElement('div');
    el.id = POPOVER_ID;
    el.setAttribute('role', 'menu');
    // Anchor: top-right of canvas, just below the glyph.
    el.style.position = 'absolute';
    el.style.top = `calc(max(${GLYPH_TOP_INSET}px, env(safe-area-inset-top) + 4px) + ${GLYPH_SIZE + 6}px)`;
    el.style.right = `${GLYPH_RIGHT_INSET}px`;
    el.style.minWidth = '180px';
    el.style.padding = '6px';
    el.style.borderRadius = '8px';
    // Anchored-to-canvas surface tier — top inset + depth lift instead
    // of a full peach border (DESIGN.md Popover component spec).
    el.style.border = 'none';
    el.style.background = 'var(--glass-fill-mid)';
    el.style.backdropFilter = 'blur(12px) saturate(1.4)';
    (el.style as unknown as { webkitBackdropFilter: string }).webkitBackdropFilter =
      'blur(12px) saturate(1.4)';
    el.style.boxShadow = [
      'inset 0 1px 0 var(--specular-edge)',
      'var(--depth-shadow-lift)',
    ].join(', ');
    el.style.zIndex = String(Z_POPOVERS);
    el.style.transformOrigin = 'top right';
    el.style.transform = 'scale(0.92)';
    el.style.opacity = '0';
    el.style.transition = 'transform 200ms cubic-bezier(0.33, 1, 0.68, 1), opacity 200ms cubic-bezier(0.33, 1, 0.68, 1)';
    el.style.userSelect = 'none';
    (el.style as unknown as { webkitUserSelect: string }).webkitUserSelect = 'none';

    el.appendChild(makeRow({
      label: 'Pokédex',
      onTap: () => {
        closePopover();
        launchOverlay('PokedexScene', 'pokedex');
      },
    }));
    el.appendChild(makeRow({
      label: 'Bag',
      onTap: () => {
        closePopover();
        launchOverlay('BagScene', 'bag');
      },
    }));
    el.appendChild(makeRow({
      label: 'Settings',
      onTap: () => {
        closePopover();
        launchOverlay('SettingsScene', 'settings');
      },
    }));

    parent.appendChild(el);
    popover = el;
    // Trigger the open animation by flipping target styles on the next
    // frame. Setting them inline immediately + relying on the transition
    // works most of the time but Chromium sometimes coalesces the
    // initial paint with the transition end; rAF guarantees a separate
    // frame.
    requestAnimationFrame(() => {
      if (!popover) return;
      popover.style.transform = 'scale(1)';
      popover.style.opacity = '1';
    });

    // Capture-phase document listener for tap-outside dismissal.
    // Capture beats any bubble-phase listener that might call
    // stopPropagation — guarantees we hear the event regardless of
    // how downstream handlers behave.
    documentDismissListener = (ev: PointerEvent): void => {
      const target = ev.target as Node | null;
      if (!target) return;
      if (popover?.contains(target)) return; // tap inside popover
      if (glyph.contains(target)) return; // tap on glyph (toggle case)
      closePopover();
    };
    document.addEventListener('pointerdown', documentDismissListener, true);
    // Esc closes the popover from anywhere (matches the modal a11y
    // baseline). Capture-phase so it beats any other Esc handler.
    documentKeydownListener = (ev: KeyboardEvent): void => {
      if (ev.key !== 'Escape') return;
      ev.preventDefault();
      ev.stopPropagation();
      closePopover();
      glyph.focus();
    };
    document.addEventListener('keydown', documentKeydownListener, true);
  }

  function closePopover(): void {
    if (!popover) return;
    const el = popover;
    popover = null;
    if (documentDismissListener) {
      document.removeEventListener('pointerdown', documentDismissListener, true);
      documentDismissListener = null;
    }
    if (documentKeydownListener) {
      document.removeEventListener('keydown', documentKeydownListener, true);
      documentKeydownListener = null;
    }
    el.style.transition = 'transform 160ms cubic-bezier(0.32, 0, 0.67, 0), opacity 160ms cubic-bezier(0.32, 0, 0.67, 0)';
    el.style.transform = 'scale(0.92)';
    el.style.opacity = '0';
    // Remove after the close transition. If the user re-opens before
    // this fires, openPopover creates a new element — leaving this
    // one's removal in the timer is fine (DOM cleanup, no leaks).
    setTimeout(() => {
      if (el.parentNode) el.parentNode.removeChild(el);
    }, 180);
  }

  // Glyph tap toggles the popover. Use pointerdown for snap response;
  // matches the orb-tap pattern + iOS Safari delay-killer convention.
  glyph.addEventListener('pointerdown', (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (isPopoverOpen()) closePopover();
    else openPopover();
  });
  // Keyboard activation (Enter/Space).
  glyph.addEventListener('keydown', (e) => {
    if (e.key !== 'Enter' && e.key !== ' ') return;
    e.preventDefault();
    if (isPopoverOpen()) closePopover();
    else openPopover();
  });

  // ─── Lifecycle ──────────────────────────────────────────────────────

  const onSleep = (): void => {
    closePopover();
    glyph.style.display = 'none';
  };
  const onWake = (): void => {
    glyph.style.display = 'flex';
  };
  scene.events.on(Phaser.Scenes.Events.SLEEP, onSleep);
  scene.events.on(Phaser.Scenes.Events.WAKE, onWake);

  return {
    destroy(): void {
      scene.events.off(Phaser.Scenes.Events.SLEEP, onSleep);
      scene.events.off(Phaser.Scenes.Events.WAKE, onWake);
      closePopover();
      glyph.remove();
    },
  };
}

// ─── Row factory ─────────────────────────────────────────────────────

interface RowOpts {
  label: string;
  subtitle?: string;
  disabled?: boolean;
  onTap?: () => void;
}

function makeRow(opts: RowOpts): HTMLElement {
  const row = document.createElement('div');
  row.setAttribute('role', 'menuitem');
  if (opts.disabled) {
    row.setAttribute('aria-disabled', 'true');
  } else {
    row.setAttribute('tabindex', '0');
  }
  row.style.display = 'flex';
  row.style.flexDirection = 'column';
  row.style.justifyContent = 'center';
  // 12/16 padding + 44px min-height clears the iOS HIG touch target
  // (was 10/14 ≈ 36px tall pre-Future-Cartridge migration).
  row.style.padding = '12px 16px';
  row.style.minHeight = '44px';
  row.style.boxSizing = 'border-box';
  row.style.borderRadius = '4px';
  row.style.fontFamily =
    "'DM Sans Variable', 'DM Sans', system-ui, -apple-system, sans-serif";
  row.style.fontSize = '14px';
  row.style.fontWeight = '500';
  row.style.color = opts.disabled ? 'var(--text-tertiary)' : 'var(--text-primary)';
  row.style.cursor = opts.disabled ? 'default' : 'pointer';
  row.style.transition = 'background 100ms ease-out';
  row.style.outline = 'none';
  row.style.userSelect = 'none';
  (row.style as unknown as { webkitUserSelect: string }).webkitUserSelect = 'none';

  const labelEl = document.createElement('span');
  labelEl.textContent = opts.label;
  row.appendChild(labelEl);

  if (opts.subtitle) {
    const sub = document.createElement('span');
    sub.textContent = opts.subtitle;
    sub.style.fontSize = '11px';
    sub.style.color = 'var(--text-secondary)';
    sub.style.marginTop = '2px';
    row.appendChild(sub);
  }

  if (!opts.disabled && opts.onTap) {
    // pointerdown not click — match the orb-tap pattern + skip iOS's
    // 300 ms synthetic-click delay. preventDefault stops the glyph
    // dismiss listener (which lives on the document at capture phase
    // and would otherwise also fire) from re-closing twice.
    row.addEventListener('pointerdown', (e) => {
      e.preventDefault();
      e.stopPropagation();
      opts.onTap!();
    });
    row.addEventListener('keydown', (e) => {
      if (e.key !== 'Enter' && e.key !== ' ') return;
      e.preventDefault();
      opts.onTap!();
    });
    row.addEventListener('mouseenter', () => {
      row.style.background = 'var(--glass-fill-thin)';
    });
    row.addEventListener('mouseleave', () => {
      row.style.background = '';
    });
    // Keyboard focus: 2px text-primary outline at -2px offset (DESIGN.md
    // Popover spec). Inline outline-on-focus, cleared on blur.
    row.addEventListener('focus', () => {
      row.style.outline = '2px solid var(--text-primary)';
      row.style.outlineOffset = '-2px';
    });
    row.addEventListener('blur', () => {
      row.style.outline = 'none';
    });
  }

  return row;
}
